/**
 * Learning Configuration Store
 *
 * Global store for caching lesson configurations by productId + classId combination.
 * Loads configuration once and reuses across all pages.
 */
import { create } from 'zustand'
import { getEffectiveConfigurationForB2B } from '@/api/configuration'
import type {
    LessonConfiguration,
    EffectiveLessonConfigurationResponse,
} from '@/types/configuration'

// Cache key for productId + classId combination
type ConfigCacheKey = `${number}-${number | 'none'}`

interface LearningConfigState {
    // Cache of configurations by productId-classId key
    configCache: Map<ConfigCacheKey, EffectiveLessonConfigurationResponse>

    // Loading states by key
    loadingKeys: Set<ConfigCacheKey>

    // Error states by key
    errors: Map<ConfigCacheKey, string>

    // Actions
    getConfiguration: (params: {
        productId: number
        classId?: number
    }) => Promise<EffectiveLessonConfigurationResponse | null>

    getCachedConfiguration: (params: {
        productId: number
        classId?: number
    }) => EffectiveLessonConfigurationResponse | null

    isLoading: (params: { productId: number; classId?: number }) => boolean

    getError: (params: { productId: number; classId?: number }) => string | null

    clearCache: () => void

    clearCacheForKey: (params: { productId: number; classId?: number }) => void
}

// Helper to create cache key
const createCacheKey = (productId: number, classId?: number): ConfigCacheKey => {
    return `${productId}-${classId ?? 'none'}`
}

export const useLearningConfigStore = create<LearningConfigState>((set, get) => ({
    configCache: new Map(),
    loadingKeys: new Set(),
    errors: new Map(),

    /**
     * Get configuration - returns from cache if available, otherwise fetches from API
     */
    getConfiguration: async ({ productId, classId }) => {
        const key = createCacheKey(productId, classId)
        const state = get()

        // Return from cache if available
        const cached = state.configCache.get(key)
        if (cached) {
            return cached
        }

        // Check if already loading
        if (state.loadingKeys.has(key)) {
            // Wait for existing request to complete
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    const currentState = get()
                    if (!currentState.loadingKeys.has(key)) {
                        clearInterval(checkInterval)
                        resolve(currentState.configCache.get(key) || null)
                    }
                }, 100)

                // Timeout after 30 seconds
                setTimeout(() => {
                    clearInterval(checkInterval)
                    resolve(null)
                }, 30000)
            })
        }

        // Start loading
        set((state) => ({
            loadingKeys: new Set(state.loadingKeys).add(key),
            errors: new Map(state.errors),
        }))

        try {
            const response = await getEffectiveConfigurationForB2B({
                productId,
                classId,
            })

            // Update cache
            set((state) => {
                const newCache = new Map(state.configCache)
                newCache.set(key, response)

                const newLoadingKeys = new Set(state.loadingKeys)
                newLoadingKeys.delete(key)

                const newErrors = new Map(state.errors)
                newErrors.delete(key)

                return {
                    configCache: newCache,
                    loadingKeys: newLoadingKeys,
                    errors: newErrors,
                }
            })

            return response
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'Failed to load configuration'

            set((state) => {
                const newLoadingKeys = new Set(state.loadingKeys)
                newLoadingKeys.delete(key)

                const newErrors = new Map(state.errors)
                newErrors.set(key, errorMessage)

                return {
                    loadingKeys: newLoadingKeys,
                    errors: newErrors,
                }
            })

            console.error('Failed to load learning configuration:', error)
            return null
        }
    },

    /**
     * Get cached configuration without fetching
     */
    getCachedConfiguration: ({ productId, classId }) => {
        const key = createCacheKey(productId, classId)
        return get().configCache.get(key) || null
    },

    /**
     * Check if configuration is currently loading
     */
    isLoading: ({ productId, classId }) => {
        const key = createCacheKey(productId, classId)
        return get().loadingKeys.has(key)
    },

    /**
     * Get error for specific configuration
     */
    getError: ({ productId, classId }) => {
        const key = createCacheKey(productId, classId)
        return get().errors.get(key) || null
    },

    /**
     * Clear all cached configurations
     */
    clearCache: () => {
        set({
            configCache: new Map(),
            loadingKeys: new Set(),
            errors: new Map(),
        })
    },

    /**
     * Clear cache for specific productId + classId
     */
    clearCacheForKey: ({ productId, classId }) => {
        const key = createCacheKey(productId, classId)
        set((state) => {
            const newCache = new Map(state.configCache)
            newCache.delete(key)

            const newErrors = new Map(state.errors)
            newErrors.delete(key)

            return {
                configCache: newCache,
                errors: newErrors,
            }
        })
    },
}))

// ============================================================================
// Hook for easy access to configuration
// ============================================================================

/**
 * Hook to get effective lesson configuration for B2B
 *
 * @example
 * const { configuration, isLoading, error } = useLearningConfig({
 *   productId: 67,
 *   classId: 18
 * })
 */
export function useLearningConfig(params: { productId: number; classId?: number } | null) {
    const store = useLearningConfigStore()

    // Don't fetch if params are null
    if (!params) {
        return {
            configuration: null as LessonConfiguration | null,
            configResponse: null as EffectiveLessonConfigurationResponse | null,
            isLoading: false,
            error: null as string | null,
            refetch: () => Promise.resolve(null),
        }
    }

    const { productId, classId } = params
    const configResponse = store.getCachedConfiguration({ productId, classId })
    const isLoading = store.isLoading({ productId, classId })
    const error = store.getError({ productId, classId })

    // Trigger fetch if not cached and not loading
    if (!configResponse && !isLoading && !error) {
        // Use setTimeout to avoid state update during render
        setTimeout(() => {
            store.getConfiguration({ productId, classId })
        }, 0)
    }

    return {
        configuration: configResponse?.configuration || null,
        configResponse,
        isLoading,
        error,
        refetch: () => store.getConfiguration({ productId, classId }),
    }
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Check if a component is enabled in the configuration
 */
export function isComponentEnabled(
    configuration: LessonConfiguration | null,
    componentType: string,
    location?: string
): boolean {
    if (!configuration) return false

    const component = configuration.sequence.find((c) => {
        if (c.type !== componentType) return false
        if (location && c.location !== location) return false
        return true
    })

    return component?.enabled ?? false
}

/**
 * Get component options from configuration
 */
export function getComponentOptions(
    configuration: LessonConfiguration | null,
    componentType: string,
    location?: string
) {
    if (!configuration) return null

    const component = configuration.sequence.find((c) => {
        if (c.type !== componentType) return false
        if (location && c.location !== location) return false
        return true
    })

    return component?.options || null
}
